/**
 * Générateur — Comparer et ranger dans l'ordre croissant et décroissant des
 * nombres décimaux relatifs.
 * Attendu ID : 915 | Niveau : 5ème | Domaine : Nombres relatifs
 *
 * Libellé officiel (spécification 915.json) : « Comparer et ranger dans l'ordre
 * croissant et décroissant des nombres décimaux relatifs. »
 *
 * Plan de cartes (version conforme à la spécification, campagne 5e 2026-09-22) :
 *
 *   Carte « comparer » — Comparer deux nombres relatifs (niveaux 1 à 4)
 *     tableau colonnesLibres à DEUX colonnes (< et >) : l'égalité n'est jamais
 *     une réponse possible, et aucune branche ne peut en produire ;
 *     qcm_unique : quatre comparaisons écrites, une seule vraie par
 *     construction ; tableau_vf : quatre comparaisons à prédicat variable.
 *     Composition imposée d'une série : deux couples de deux négatifs, un
 *     couple piège sur les rangs décimaux, un couple de signes contraires, un
 *     couple d'entiers relatifs.
 *
 *   Carte « ranger » — Ranger dans l'ordre (niveaux 5 et 6)
 *     Quatre formats, pour que les deux ordres soient réglables séparément :
 *     ordonner (croissant / décroissant) et texte_trou_saisie (croissant /
 *     décroissant), la chaîne étant écrite avec le symbole d'ordre imposé et
 *     chaque case comparée séparément.
 *
 *   Carte « intrus » — Le nombre qui n'est pas à sa place (niveaux 5 et 6)
 *     qcm_unique. Unicité PAR CONSTRUCTION : on part d'une liste triée et on
 *     déplace un élément d'au moins deux rangs — le retirer répare la chaîne,
 *     retirer tout autre la laisse cassée.
 *
 * Spécification (915.json) :
 *   Bornes appliquées : type_des_nombres = décimal relatif (`origine:
 *     prescrit`), les entiers n'étant qu'un niveau d'entrée ;
 *     ordres_travailles = croissant ET décroissant (`origine: prescrit`),
 *     réglables séparément (un format par sens) ;
 *     nombre_de_decimales 1..2 hors du niveau d'entrée ;
 *     nombres_compares -100..100 ; nombre_de_nombres_a_ranger 4..6 ;
 *     ecart_entre_deux_nombres_a_ranger 0,01..2 ;
 *     part_de_couples_de_meme_signe_negatif 0,3..0,5 et
 *     part_de_couples_pieges_sur_les_rangs_decimaux 0,2..0,33 : les deux sont
 *     EXACTES à chaque tirage (composition fixée d'une série : 2 couples de
 *     deux négatifs et 1 couple piège sur 5 lignes, 2 et 1 sur 4 propositions) ;
 *     presence_d_egalites_dans_une_liste = toujours faux.
 *   Exclusions : aucune égalité, ni dans une liste ni entre les deux nombres
 *     d'une comparaison (valeurs distinctes par construction, contrôlées après
 *     application du signe) ; les couples qui visent les rangs décimaux
 *     partagent la MÊME partie entière ; aucun nombre à plus de deux
 *     décimales ; aucune liste à ranger de signes tous identiques ; le symbole
 *     d'égalité n'est jamais une réponse (la colonne « = » a disparu du
 *     tableau, la consigne annonce « < ou > »).
 *   Erreurs visées → mots-clés (_meta.params.distracteurs[].code,
 *   _meta.params.lignes[].code, _meta.params.couples[].code, erreurVisee) :
 *     « Comparer deux négatifs d'après leur distance à zéro »
 *         → negatifs_compares_par_distance (comparer ×3, intrus)
 *     « Croire que le nombre qui a le plus de chiffres décimaux est le plus grand »
 *         → plus_de_chiffres_plus_grand (comparer ×3)
 *     « Ranger les valeurs absolues puis oublier d'inverser sur la partie négative »
 *         → ordre_des_absolues_non_inverse (ranger ×4, intrus)
 *     « Lire -3,2 comme différent de -3,20 »
 *         → zeros_finaux_refuses (ranger ×4 : les listes mêlent une et deux
 *           décimales, et la tolérance numérique de la saisie accepte les deux
 *           écritures, ce que la consigne annonce)
 *     « Passer de l'ordre croissant à l'ordre décroissant sans relire la consigne »
 *         → sens_de_rangement_ignore (ranger ×4, intrus)
 *     « Conclure d'un signe à un rang sans comparer »
 *         → signe_conclu_sans_comparer (comparer ×3)
 *   Graduation → cartes :
 *     niveau 1 (deux entiers relatifs) → cas `entiers` de la carte comparer ;
 *     niveau 2 (une décimale, signes contraires) → cas `mixte` ;
 *     niveau 3 (deux décimaux négatifs à une décimale) → cas `neg_neg` ;
 *     niveau 4 (deux décimales, le plus long est le plus petit) → cas `rangs` ;
 *     niveau 5 (ranger 4 à 6 décimaux de signes mêlés, croissant) →
 *       ranger_croissant_* et intrus ;
 *     niveau 6 (même liste, décroissant, nombres serrés au centième) →
 *       ranger_decroissant_* et intrus (écarts tirés dès 0,01).
 *   Non tranché : `nombre_de_decimales` min 1 contredit le niveau 1 de la
 *     graduation (« deux entiers relatifs ») ; le cas piège sur les rangs
 *     décimaux est servi sur des positifs, faute de quoi les deux proportions
 *     déclarées ne peuvent pas tenir ensemble ; « série » de questions n'a pas
 *     d'équivalent (un tirage = une question).
 *
 * Arithmétique en CENTIÈMES entiers. Signe « - » = tiret clavier partout.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeOrdonner, makeTexteTrou, makeVraiFaux, basePayload,
} from '../../../utils';

const ATTENDU_ID = 915;

const L_COMPARER = 'Comparer deux nombres relatifs';
const L_RANGER = 'Ranger dans l\'ordre';
const L_INTRUS = 'Le nombre qui n\'est pas à sa place';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

/** Centièmes entiers → écriture française (« -9,27 », « -3,5 », « 12 »). */
const cent = (c) => {
    const s = c < 0 ? '-' : '';
    const a = Math.abs(c);
    const ent = Math.floor(a / 100);
    const r = a % 100;
    if (r === 0) return `${s}${ent}`;
    if (r % 10 === 0) return `${s}${ent},${r / 10}`;
    return `${s}${ent},${String(r).padStart(2, '0')}`;
};

const MAX = 10000; // 100 en centièmes (nombres_compares -100..100)

// ═════════ Carte « comparer » ═════════

/**
 * Un couple (a, b) de centièmes, selon le cas de la graduation.
 * `entiers`  : deux entiers relatifs distincts, jamais deux négatifs (niveau 1) ;
 * `mixte`    : signes contraires, une décimale, valeurs absolues proches (niveau 2) ;
 * `neg_neg`  : deux négatifs à une décimale, MÊME partie entière (niveau 3) ;
 * `rangs`    : même partie entière, le nombre à deux décimales étant le plus
 *              petit — « 3,4 et 3,08 » (niveau 4).
 * Aucun cas ne peut produire une égalité.
 */
const tirerCouple = (cas) => {
    if (cas === 'entiers') {
        const a = randInt(1, 99);
        let b = randInt(1, 99);
        if (b === a) b = a === 99 ? 1 : a + 1; // repli déterministe
        // jamais deux négatifs : le niveau 1 reste un niveau d'entrée
        return randomChoice([[-a * 100, b * 100], [a * 100, -b * 100], [a * 100, b * 100]]);
    }
    if (cas === 'mixte') {
        const e1 = randInt(0, 98);
        const d1 = randInt(1, 9);
        const e2 = Math.min(98, Math.max(0, e1 + randomChoice([-1, 0, 1])));
        const d2 = randInt(1, 9);
        const neg = -(e1 * 100 + d1 * 10);
        const pos = e2 * 100 + d2 * 10;
        return randomChoice([[neg, pos], [pos, neg]]);
    }
    if (cas === 'neg_neg') {
        const e = randInt(0, 98);
        const d1 = randInt(1, 9);
        let d2 = randInt(1, 9);
        if (d2 === d1) d2 = d1 === 9 ? 1 : d1 + 1; // repli déterministe
        const a = -(e * 100 + d1 * 10);
        const b = -(e * 100 + d2 * 10);
        return randomChoice([[a, b], [b, a]]);
    }
    // `rangs` : positif, pour que les deux proportions déclarées tiennent ensemble
    const e = randInt(0, 98);
    const dixieme = randInt(1, 9);
    const centieme = randInt(1, 9);
    const court = e * 100 + dixieme * 10;   // deux chiffres après la virgule : 3,4
    const long = e * 100 + centieme;        // trois chiffres à l'écrit : 3,08, et pourtant plus petit
    return randomChoice([[court, long], [long, court]]);
};

/**
 * Les couples d'une série, tous DISTINCTS : deux lignes identiques dans un
 * tableau sont dédoublonnées par le rendu, et deux propositions identiques dans
 * un QCM sont indiscernables.
 */
const cleCouple = ([a, b]) => `${a}|${b}`;
const tirerCouplesDistincts = (cases) => {
    const vus = new Set();
    return cases.map((c) => {
        let paire = tirerCouple(c);
        for (let essai = 0; essai < 30 && vus.has(cleCouple(paire)); essai++) paire = tirerCouple(c);
        // Repli déterministe : on éloigne le couple d'une unité, dans son sens,
        // ce qui conserve la relation d'ordre et les bornes.
        if (vus.has(cleCouple(paire))) paire = paire.map(v => v + (v < 0 ? -100 : 100));
        vus.add(cleCouple(paire));
        return { cas: c, code: CODE_CAS[c], paire };
    });
};

/** L'erreur d'élève que chaque cas matérialise. */
const CODE_CAS = {
    entiers: 'signe_conclu_sans_comparer',
    mixte: 'signe_conclu_sans_comparer',
    neg_neg: 'negatifs_compares_par_distance',
    rangs: 'plus_de_chiffres_plus_grand',
};

/**
 * Composition d'une série de comparaisons. Sur cinq lignes : deux couples de
 * deux négatifs (0,4, dans 0,3..0,5) et un couple piège sur les rangs décimaux
 * (0,2, dans 0,2..0,33). Sur quatre propositions : 0,5 et 0,25.
 */
const COMPOSITION = {
    5: ['neg_neg', 'neg_neg', 'rangs', 'mixte', 'entiers'],
    4: ['neg_neg', 'neg_neg', 'rangs', 'mixte'],
};

const CONSIGNE_SANS_EGAL = 'Les deux nombres ne sont jamais égaux : la réponse est toujours &lt; ou &gt;.';

const genComparerTableau = () => {
    const couples = tirerCouplesDistincts(shuffle([...COMPOSITION[5]]));
    const colonnes = ['<', '>'];
    const lignes = couples.map(({ paire: [a, b] }) => `${cent(a)} … ${cent(b)}`);
    const bonnes = couples.map(({ paire: [a, b] }) => (a < b ? 0 : 1));
    const tableEl = {
        type: 'table',
        config: { type: 'colonnesLibres', colonnes, lignes, bonnesReponses: bonnes },
    };
    const br = lignes.map((l, i) => `[${l}|${colonnes[bonnes[i]]}]`).join('');
    const payload = basePayload('qcm',
        `<p><b>Pour chaque ligne, coche le symbole qui convient.</b> ${CONSIGNE_SANS_EGAL}</p>`,
        { choix: [], bonne_reponse: br },
        opts(L_COMPARER),
        { version: 1, elements: [tableEl] });
    payload._meta.params = { couples, niveau: 4 };
    return payload;
};

/**
 * « a < b » ou « a > b », vraie ou fausse selon `vrai`.
 *
 * ⚠️ Les symboles sont écrits EN CLAIR, pas en entité HTML. Cette chaîne sert de
 * PROPOSITION de QCM, et une proposition est rendue par MathText, qui l'affiche comme
 * du texte : l'entité y restait littérale, l'élève lisait « 28,04 &gt; 28,8 » (signalé
 * par le prof le 2026-09-25). Aucun chemin n'injecte une proposition comme du HTML,
 * ni à l'écran ni à l'impression, le chevron brut y est donc sans danger.
 *
 * L'entité reste nécessaire ailleurs dans ce fichier : dans un ÉNONCÉ, qui est du HTML,
 * et dans une phrase à trous, où un « < » brut serait pris pour une balise.
 */
const ecrireComparaison = (a, b, vrai) => {
    const juste = a < b ? '<' : '>';
    const faux = a < b ? '>' : '<';
    return `${cent(a)} ${vrai ? juste : faux} ${cent(b)}`;
};

const genComparerQcm = () => {
    // La consigne demande tantôt la comparaison VRAIE, tantôt la FAUSSE : sans
    // cette rotation, un couple de signes contraires (niveau 2 de la graduation)
    // serait décidable d'après sa seule forme, et l'élève répondrait sans comparer.
    const chercheVraie = randomChoice([true, false]);
    const iCible = randInt(0, 3);
    const couples = tirerCouplesDistincts(shuffle([...COMPOSITION[4]])).map((c, i) => {
        const vrai = i === iCible ? chercheVraie : !chercheVraie;
        return { ...c, vrai, cible: i === iCible, texte: ecrireComparaison(c.paire[0], c.paire[1], vrai) };
    });
    const correct = couples[iCible].texte;
    const payload = makeQcm(couples.map(c => c.texte), correct,
        `<p><b>Quelle comparaison est ${chercheVraie ? 'vraie' : 'fausse'} ?</b> ${CONSIGNE_SANS_EGAL}</p>`,
        opts(L_COMPARER));
    payload._meta.params = {
        couples, chercheVraie, niveau: 4,
        distracteurs: couples.filter(c => !c.cible).map(c => ({ valeur: c.texte, code: c.code })),
    };
    return payload;
};

const genComparerVf = () => {
    const cas = shuffle([...COMPOSITION[4]]);
    const verites = [];
    for (let i = 0; i < 4; i++) verites.push(randomChoice([true, false]));
    if (verites.every(v => v)) verites[randInt(0, 3)] = false;
    else if (verites.every(v => !v)) verites[randInt(0, 3)] = true;
    const couples = tirerCouplesDistincts(cas).map((c, i) => ({
        ...c, template: c.cas, vrai: verites[i],
        texte: `${cent(c.paire[0])} est plus ${(c.paire[0] < c.paire[1]) === verites[i] ? 'petit' : 'grand'} que ${cent(c.paire[1])}.`,
    }));
    const payload = makeVraiFaux(
        couples.map(c => c.texte),
        couples.map(c => (c.vrai ? 0 : 1)),
        '<p><b>Pour chaque affirmation, coche Vrai ou Faux.</b> Les deux nombres comparés ne sont jamais égaux.</p>',
        opts(L_COMPARER));
    payload._meta.params = { couples, lignes: couples, niveau: 4 };
    return payload;
};

// ═════════ Carte « ranger » ═════════

/** Liste de repli, conforme : 5 valeurs, signes mêlés, écarts 1,15 à 1,45. */
const LISTE_REPLI = [-350, -205, -70, 45, 160];

/**
 * 4 à 6 décimaux relatifs, triés croissant, de signes MÊLÉS, à écarts
 * consécutifs compris entre 0,01 et 2, jamais entiers (une ou deux décimales),
 * jamais égaux.
 */
const tirerListe = () => {
    for (let essai = 0; essai < 60; essai++) {
        const n = randInt(4, 6);
        const ecarts = [];
        for (let i = 0; i < n - 1; i++) ecarts.push(randInt(2, 200));
        const k = randInt(1, n - 1);                 // vals[k] est le premier positif
        const d = randInt(1, ecarts[k - 1] - 1);     // 0 < vals[k] < écart précédent
        const vals = [];
        let v = d;
        for (let i = k; i < n; i++) { vals[i] = v; v += ecarts[i] || 0; }
        v = d;
        for (let i = k - 1; i >= 0; i--) { v -= ecarts[i]; vals[i] = v; }
        if (vals.some(x => x % 100 === 0 || x === 0 || Math.abs(x) > MAX)) continue;
        if (new Set(vals).size !== n) continue;
        return vals;
    }
    return [...LISTE_REPLI]; // repli déterministe, vérifié à la main
};

const MOT_ORDRE = {
    // Deux écritures du même symbole, selon le contexte de rendu :
    //   `symbole`     — TEXTE : phrase à trous, étiquettes. Le chevron brut s'y affiche
    //                   correctement ; l'entité y restait littérale devant l'élève.
    //   `symboleHtml` — ÉNONCÉ : le texte de l'énoncé est injecté comme du HTML, un
    //                   chevron brut y serait pris pour une balise.
    croissant: { adj: 'croissant', aide: 'du plus petit au plus grand', symbole: '<', symboleHtml: '&lt;' },
    decroissant: { adj: 'décroissant', aide: 'du plus grand au plus petit', symbole: '>', symboleHtml: '&gt;' },
};

const CONSIGNE_ECRITURES = 'Les deux écritures d\'un même nombre sont acceptées : -3,2 vaut -3,20.';

const genRangerOrdonner = (sens) => () => {
    const vals = tirerListe();
    const o = MOT_ORDRE[sens];
    const tries = sens === 'croissant' ? vals : [...vals].reverse();
    const payload = makeOrdonner(tries.map(cent),
        `<p><b>Range ces nombres dans l'ordre ${o.adj} (${o.aide}).</b></p>`,
        opts(L_RANGER));
    payload._meta.params = {
        vals, sens, niveau: sens === 'croissant' ? 5 : 6,
        erreurVisee: sens === 'croissant' ? 'ordre_des_absolues_non_inverse' : 'sens_de_rangement_ignore',
    };
    return payload;
};

const genRangerTrou = (sens) => () => {
    const vals = tirerListe();
    const o = MOT_ORDRE[sens];
    const tries = sens === 'croissant' ? vals : [...vals].reverse();
    // Le symbole est ici EN CLAIR : la phrase à trous est rendue comme du texte, le
    // chevron s'y affiche tel quel et les champs de saisie restent intacts. L'entité y
    // était lue littéralement par l'élève (retour prof 2026-09-25) ; le commentaire qui
    // la justifiait était faux, vérifié sur le vrai composant.
    const phrase = tries.map(m => `{{${cent(m)}}}`).join(` ${o.symbole} `);
    const payload = makeTexteTrou(phrase,
        `<p><b>Range ces nombres dans l'ordre ${o.adj} (${o.aide}) : `
        + `${shuffle(vals.map(cent)).join(' ; ')}.</b> ${CONSIGNE_ECRITURES}</p>`,
        { ...opts(L_RANGER), mode: 'saisie' });
    payload._meta.params = {
        vals, sens, niveau: sens === 'croissant' ? 5 : 6,
        erreurVisee: sens === 'croissant' ? 'zeros_finaux_refuses' : 'sens_de_rangement_ignore',
    };
    return payload;
};

// ═════════ Carte « intrus » ═════════

const genIntrusQcm = () => {
    const vals = tirerListe();
    const sens = randomChoice(['croissant', 'decroissant']);
    const o = MOT_ORDRE[sens];
    const triees = sens === 'croissant' ? vals : [...vals].reverse();
    // Un élément déplacé d'au moins DEUX rangs : le retirer répare la chaîne,
    // retirer tout autre la laisse cassée.
    const k = randomChoice([0, 1]);
    const p = Math.min(triees.length - 1, k + randomChoice([2, 3]));
    const intrus = triees[k];
    const chaine = triees.filter((_, i) => i !== k);
    chaine.splice(p, 0, intrus);

    const autres = chaine.filter(m => m !== intrus).map(cent);
    const payload = makeQcm([cent(intrus), ...shuffle(autres).slice(0, 3)], cent(intrus),
        '<p>Cette chaîne est presque rangée, mais un nombre n\'est pas à sa place :</p>'
        + `<p><strong>${chaine.map(cent).join(` ${o.symboleHtml} `)}</strong></p>`
        + `<p><b>Quel nombre faut-il retirer pour que la chaîne devienne vraie ?</b> `
        + `La chaîne est écrite dans l'ordre ${o.adj}.</p>`,
        opts(L_INTRUS));
    payload._meta.params = {
        vals, sens, intrus, chaine, niveau: sens === 'croissant' ? 5 : 6,
        distracteurs: shuffle(autres).slice(0, 3).map(v => ({ valeur: v, code: 'ordre_des_absolues_non_inverse' })),
        erreurVisee: 'sens_de_rangement_ignore',
    };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    comparer_tableau: genComparerTableau,
    comparer_qcm_unique: genComparerQcm,
    comparer_vf: genComparerVf,
    ranger_croissant_ordonner: genRangerOrdonner('croissant'),
    ranger_decroissant_ordonner: genRangerOrdonner('decroissant'),
    ranger_croissant_texte_trou_saisie: genRangerTrou('croissant'),
    ranger_decroissant_texte_trou_saisie: genRangerTrou('decroissant'),
    intrus_qcm_unique: genIntrusQcm,
};

const FORMAT_LABELS = {
    tableau: 'Tableau', qcm_unique: 'QCM', vf: 'Vrai/Faux',
    croissant_ordonner: 'Ordonner (croissant)', decroissant_ordonner: 'Ordonner (décroissant)',
    croissant_texte_trou_saisie: 'Chaîne (croissant)', decroissant_texte_trou_saisie: 'Chaîne (décroissant)',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'comparer', label: L_COMPARER, formatsList: buildFormats('comparer') },
    { id: 'ranger', label: L_RANGER, formatsList: buildFormats('ranger') },
    { id: 'intrus', label: L_INTRUS, formatsList: buildFormats('intrus') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
